import * as React from 'react';

import ContractSelectionStep from 'bundles/enterprise-admin-learners/components/invite-learners/ContractSelectionStep';
import CsvInviteModal from 'bundles/enterprise-admin-learners/components/invite-learners/CsvInviteModal';
import DomainAllowlistModal from 'bundles/enterprise-admin-learners/components/invite-learners/DomainAllowlistModal';
import EmailInviteModal from 'bundles/enterprise-admin-learners/components/invite-learners/EmailInviteModal';
import InviteMethodModal from 'bundles/enterprise-admin-learners/components/invite-learners/InviteMethodModal';
import { getAutoSelectedGroupId } from 'bundles/enterprise-admin-learners/components/invite-learners/getAutoSelectedGroupId';
import type {
  InviteEntryPoint,
  InviteMethod,
} from 'bundles/enterprise-admin-learners/components/invite-learners/types';
import { useManageableGroups } from 'bundles/enterprise-admin-learners/hooks/useManageableGroups';
import { useContractsByOrgId } from 'bundles/enterprise-admin-program-catalog/hooks/useContractsByOrgId';
import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';

type Step = 'method' | 'contract' | InviteMethod;

export type InviteLearnersModalProps = {
  open: boolean;
  onClose: () => void;
  entryPoint: InviteEntryPoint;
  /** Pre-selects (and, with `lockGroupSelection`, fixes) this group — Group Details only. */
  defaultGroupId?: string;
  /** True on Group Details: the group is fixed, not just pre-selected. */
  lockGroupSelection?: boolean;
  /** Refetch/callback fired once invites are sent — only the email path fires this today. */
  onInvited: () => void;
};

/**
 * Entry point for the org-level invite flow (ENTADMIN-1951), opened identically from
 * Home, All Learners, and Group Details. Owns the method → email/CSV/domain step
 * state; each step renders as its own full dialog (see `AddToGroupModal` for the same
 * pattern), so this component has no chrome of its own.
 *
 * The email/CSV/domain sub-flows are scaffolding placeholders here — they're built out
 * in later Phase A tasks. This component's job is the step machine, the entry-point
 * copy switch, and the auto-select-and-skip computation for Group admins who manage
 * exactly one group.
 */
const InviteLearnersModal = ({
  open,
  onClose,
  entryPoint,
  defaultGroupId,
  lockGroupSelection,
  onInvited,
}: InviteLearnersModalProps) => {
  const [step, setStep] = React.useState<Step>('method');
  const [pendingMethod, setPendingMethod] = React.useState<InviteMethod | null>(null);
  const [selectedContractId, setSelectedContractId] = React.useState<string | undefined>(undefined);
  const manageableGroups = useManageableGroups();
  const { getIsOrganizationDomainAdmin } = useEnterpriseAdminPermissions();
  const isGroupAdmin = !getIsOrganizationDomainAdmin();
  const { contractOptions } = useContractsByOrgId();

  // Reset to the method-selection step whenever the modal is (re)opened.
  React.useEffect(() => {
    if (open) {
      setStep('method');
      setPendingMethod(null);
      setSelectedContractId(undefined);
    }
  }, [open]);

  // Consumed starting with GroupAssignmentStep (Task 5): the group to skip the picker
  // for, either because Group Details locked it or a Group admin manages just one group.
  const autoSelectedGroupId = getAutoSelectedGroupId({
    lockGroupSelection,
    defaultGroupId,
    isGroupAdmin,
    manageableGroups,
  });

  const onBackToMethod = () => setStep('method');

  // Domain allowlisting is excluded — per-contract domain mapping is out of scope
  // (TRD non-goals), so it never gates on a contract choice.
  const handleSelectMethod = (method: InviteMethod) => {
    // contractOptions === undefined means the query hasn't resolved yet — route through
    // the contract step's own loading state rather than assuming a single-contract org
    // and submitting unscoped, per the effect below which skips forward once it resolves.
    if (method !== 'domain' && (contractOptions === undefined || contractOptions.length > 1)) {
      setPendingMethod(method);
      setStep('contract');
    } else {
      setStep(method);
    }
  };

  // Once the contract count resolves to 0 or 1 while sitting on the (loading) contract
  // step, there's nothing to choose — advance straight to the pending method instead of
  // making the admin click through a single-option picker.
  React.useEffect(() => {
    if (step === 'contract' && pendingMethod && contractOptions && contractOptions.length <= 1) {
      setStep(pendingMethod);
    }
  }, [step, pendingMethod, contractOptions]);

  const handleSelectContract = (contractId: string) => {
    setSelectedContractId(contractId);
    if (pendingMethod) setStep(pendingMethod);
  };

  // Each step renders as its own full dialog; keyed by `step` instead of a
  // chain of `if` returns so adding a step is a new entry, not a new branch.
  const stepScreens: Record<Step, () => JSX.Element> = {
    method: () => (
      <InviteMethodModal open={open} onClose={onClose} entryPoint={entryPoint} onSelectMethod={handleSelectMethod} />
    ),
    contract: () => <ContractSelectionStep open={open} onBack={onBackToMethod} onSelect={handleSelectContract} />,
    email: () => (
      <EmailInviteModal
        open={open}
        onClose={onClose}
        onBack={onBackToMethod}
        autoSelectedGroupId={autoSelectedGroupId}
        isOrgAdminNoGroup={!isGroupAdmin && autoSelectedGroupId === undefined}
        selectedContractId={selectedContractId}
        onSwitchToCsv={() => setStep('csv')}
        onInvited={onInvited}
      />
    ),
    csv: () => (
      <CsvInviteModal
        open={open}
        onClose={onClose}
        onBack={onBackToMethod}
        autoSelectedGroupId={autoSelectedGroupId}
        selectedContractId={selectedContractId}
      />
    ),
    domain: () => (
      <DomainAllowlistModal
        open={open}
        onClose={onClose}
        onBack={onBackToMethod}
        autoSelectedGroupId={autoSelectedGroupId}
      />
    ),
  };

  return stepScreens[step]();
};

export default InviteLearnersModal;
